import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Skeleton,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  ResponsiveContainer,
} from "recharts";
import configServ from "../../services/config";
import { useSelector } from "react-redux";

const formatYAxis = (value) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value;
};

const ChartCard = ({ title, loading, children }) => (
  <Card sx={{ p: 2, borderRadius: 4, boxShadow: 3, width: "100%" }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {loading ? (
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
      ) : (
        children
      )}
    </CardContent>
  </Card>
);

// Daily Line Chart with Pagination
const PerDayLineChart = ({ title, data, color, loading }) => {
  const chunkSize = 7;
  const [startIndex, setStartIndex] = useState(0);
  const paginatedData = data.slice(startIndex, startIndex + chunkSize);

  return (
    <ChartCard title={title} loading={loading}>
      {!loading && (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={paginatedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="day"
                interval={0}
                angle={-32}
                dy={4}
                textAnchor="end"
                height={60}
              />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setStartIndex(startIndex - chunkSize)}
              disabled={startIndex === 0}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setStartIndex(startIndex + chunkSize)}
              disabled={startIndex + chunkSize >= data.length}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </ChartCard>
  );
};

// Monthly Bar Chart with Pagination
const MonthlyBarChart = ({ title, data, color, loading }) => {
  const chunkSize = 6;
  const [startIndex, setStartIndex] = useState(0);
  const paginatedData = data.slice(startIndex, startIndex + chunkSize);

  return (
    <ChartCard title={title} loading={loading}>
      {!loading && (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={paginatedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                interval={0}
                angle={-45}
                dy={2}
                textAnchor="end"
                height={60}
              />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip />
              <Bar dataKey="value" fill={color} />
            </BarChart>
          </ResponsiveContainer>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setStartIndex(startIndex - chunkSize)}
              disabled={startIndex === 0}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setStartIndex(startIndex + chunkSize)}
              disabled={startIndex + chunkSize >= data.length}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </ChartCard>
  );
};

// Main Dashboard Component
const InvoiceDashboardCharts = () => {
  const [count, setCount] = useState({});
  const [loading, setLoading] = useState(true);
  const { user_id, company_id } = useSelector((state) => state.GlobalVariables);

  const fetchDashboardCountGraph = async () => {
    try {
      setLoading(true);
      const result = await configServ.getAllTimeReports({company_id:company_id});
      if (result.success) {
        setCount(result);
      } else {
        console.warn("Data fetch unsuccessful");
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardCountGraph();
  }, []);

  const monthlyInvoiceData =
    count.monthlyInvoices?.map((item) => {
      const monthYear = new Date(item.month + "-01").toLocaleString("default", {
        month: "short",
        // year: "numeric",
      });
      return {
        month: monthYear,
        value: item.total_invoices,
      };
    }) || [];

  const monthlySalesData =
    count.monthlySales?.map((item) => {
      const monthYear = new Date(item.month + "-01").toLocaleString("default", {
        month: "short",
        // year: "numeric",
      });
      return {
        month: monthYear,
        value: parseFloat(item.total_sales),
      };
    }) || [];

    const monthlyVouturesData =
    count.monthlyVouchers?.map((item) => {
      const monthYear = new Date(item.month + "-01").toLocaleString("default", {
        month: "short",
        // year: "numeric",
      });
      return {
        month: monthYear,
        value: parseFloat(item.total_sales),
      };
    }) || [];

    const fullPerDayInvoiceData =
    count.dailyInvoices?.map((item) => {
      const formattedDate = new Date(item.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        // year: "2-digit",
      }); // Output: "11 Apr 25"
      return {
        day: formattedDate,
        value: item.total_invoices,
      };
    }) || [];
  

    const fullPerDaySalesData =
    count.dailySales?.map((item) => {
      const formattedDate = new Date(item.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        // year: "2-digit",
      });
      return {
        day: formattedDate,
        value: parseFloat(item.total_sales),
      };
    }) || [];

    const fullPerDayVouturesData =
    count.dailyVouchers?.map((item) => {
      const formattedDate = new Date(item.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        // year: "2-digit",
      });
      return {
        day: formattedDate,
        value: parseFloat(item.total_sales),
      };
    }) || [];
  

  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gap: 4,
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      }}
    >
      <PerDayLineChart
        title=" Daily Sale's"
        data={fullPerDaySalesData}
        color="#29b6f6"
        loading={loading}
      />
      <MonthlyBarChart
        title="Monthly Sale's"
        data={monthlySalesData}
        color="#388e3c"
        loading={loading}
      />
      <PerDayLineChart
        title=" Daily Cash's"
        data={fullPerDayVouturesData}
        color="#29b6f6"
        loading={loading}
      />
      <MonthlyBarChart
        title="Monthly cash's"
        data={monthlyVouturesData}
        color="#388e3c"
        loading={loading}
      />
      <PerDayLineChart
        title="Daily Invoice Created"
        data={fullPerDayInvoiceData}
        color="#ff7043"
        loading={loading}
      />
      <MonthlyBarChart
        title="Monthly Invoice Created"
        data={monthlyInvoiceData}
        color="#1976d2"
        loading={loading}
      />
    </Box>
  );
};

export default InvoiceDashboardCharts;
